import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getEventBySlug, getEventGames, Event, Game } from "@/services/eventService";
import { useAuth } from "@/hooks/useAuth";
import { GameLikeButton } from "@/components/GameLikeButton";
import { GameFavoriteButton } from "@/components/GameFavoriteButton";
import { GameRequestButton } from "@/components/GameRequestButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export default function EventPage() {
    const { eventSlug } = useParams<{ eventSlug: string }>();
    const navigate = useNavigate();
    const { user, userType } = useAuth();
    const [event, setEvent] = useState<Event | null>(null);
    const [games, setGames] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);

    // Request Dialog State
    const [isRequestingKey, setIsRequestingKey] = useState(false);
    const [requestMessage, setRequestMessage] = useState("");
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
    const [selectedGameForRequest, setSelectedGameForRequest] = useState<Game | null>(null);

    useEffect(() => {
        if (eventSlug) {
            loadEvent();
        }
    }, [eventSlug]);

    const loadEvent = async () => {
        if (!eventSlug) return;

        try {
            setLoading(true);
            const eventData = await getEventBySlug(eventSlug);

            if (!eventData) {
                toast.error("Event not found or not currently active");
                navigate("/dashboard");
                return;
            }

            setEvent(eventData);

            // Load games for this event
            const eventGames = await getEventGames(eventData.selected_genres);

            // Check if there are at least 3 eligible games
            if (eventGames.length < 3) {
                toast.error("Not enough games available for this event");
                navigate("/dashboard");
                return;
            }

            setGames(eventGames);
        } catch (error) {
            console.error("Error loading event:", error);
            toast.error("Failed to load event");
            navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    };

    const handleViewGameDetail = (game: Game) => {
        navigate(`/game/${game.id}`);
    };

    const openRequestDialog = (game: Game) => {
        setSelectedGameForRequest(game);
        setIsRequestingKey(true);
    };

    const handleRequestKey = async () => {
        if (!user || !selectedGameForRequest) return;

        setIsSubmittingRequest(true);
        try {
            const { error } = await supabase
                .from("key_requests")
                .insert({
                    game_id: selectedGameForRequest.id,
                    creator_id: user.id,
                    developer_id: selectedGameForRequest.developer.id,
                    message: requestMessage.trim() || null
                });

            if (error) {
                if (error.code === "23505") {
                    toast.error("You've already requested a key for this game");
                } else {
                    toast.error(error.message);
                }
            } else {
                toast.success("Key request submitted successfully!");
                setIsRequestingKey(false);
                setRequestMessage("");
                setSelectedGameForRequest(null);
            }
        } catch (error) {
            console.error('Error requesting key:', error);
            toast.error("Error submitting key request");
        } finally {
            setIsSubmittingRequest(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[calc(100vh-120px)]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    if (!event) {
        return null;
    }

    return (
        <Layout>
            <div className="container mx-auto px-4 py-8">
                {/* Banner */}
                {event.banner_image_url && (
                    <div className="mb-8">
                        <img
                            src={event.banner_image_url}
                            alt="Event banner"
                            className="w-full h-64 object-cover rounded-lg"
                        />
                    </div>
                )}

                {/* WYSIWYG Content */}
                {event.wysiwyg_content && (
                    <div
                        className="prose max-w-none mb-8 text-center wysiwyg-content"
                        dangerouslySetInnerHTML={{ __html: event.wysiwyg_content }}
                    />
                )}

                {/* Games Section */}
                <div className="mb-6">
                    <h2 className="text-2xl font-bold mb-0">Our selection matching this event</h2>
                    <p className="text-muted-foreground text-xs">
                        Showing {games.length} game{games.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {games.length === 0 ? (
                    <Card className="p-8 text-center">
                        <p className="text-muted-foreground">No games available for this event.</p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {games.map((game) => (
                            <Card key={game.id} className="overflow-hidden hover:shadow-lg transition-shadow rounded-custom-paddings">
                                <div
                                    className="relative cursor-pointer"
                                    onClick={() => handleViewGameDetail(game)}
                                >
                                    <img
                                        src={game.cover_image_url || '/placeholder.svg'}
                                        alt={game.title}
                                        className="w-full h-32 object-cover rounded-custom-paddings p-0"
                                    />
                                </div>
                                <div className="p-3">
                                    <h3
                                        className="font-semibold mb-1 text-sm line-clamp-1 cursor-pointer hover:text-primary"
                                        onClick={() => handleViewGameDetail(game)}
                                    >
                                        {game.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {game.genres.slice(0, 3).map((genre, idx) => (
                                            <Badge key={idx} variant="outline" className="text-xs">
                                                {genre}
                                            </Badge>
                                        ))}
                                        {game.genres.length > 3 && (
                                            <Badge variant="outline" className="text-xs">
                                                +{game.genres.length - 3}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex gap-1">
                                            {userType === 'player' ? (
                                                <>
                                                    <GameLikeButton gameId={game.id} />
                                                    <GameFavoriteButton gameId={game.id} />
                                                </>
                                            ) : (
                                                <>
                                                    {/* <GameRequestButton
                                                    gameId={game.id}
                                                    gameTitle={game.title}
                                                    allowRequests={game.allow_requests}
                                                    availableKeys={game.available_keys}
                                                    onRequestClick={() => openRequestDialog(game)}
                                                    className="text-xs"
                                                /> */}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Request Key Dialog */}
                <Dialog open={isRequestingKey} onOpenChange={setIsRequestingKey}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Request Key for {selectedGameForRequest?.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="request-message">Message (optional)</Label>
                                <Textarea
                                    id="request-message"
                                    value={requestMessage}
                                    onChange={(e) => setRequestMessage(e.target.value)}
                                    placeholder="Tell the developer why you'd like a key for this game..."
                                    rows={4}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleRequestKey}
                                    disabled={isSubmittingRequest}
                                    className="flex-1"
                                >
                                    {isSubmittingRequest ? "Submitting..." : "Submit Request"}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setIsRequestingKey(false);
                                        setRequestMessage("");
                                        setSelectedGameForRequest(null);
                                    }}
                                    disabled={isSubmittingRequest}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
